import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { GlobalShortcutsItem, ShortcutsConfig } from '../../types';

import type { useGlobalShortcutsDataQuery as useGlobalShortcutsDataQueryType } from './__generated__/useGlobalShortcutsDataQuery.graphql';
import type { InferShortcutsOutput, UseGlobalShortcutDataInputBase } from './types';
import { useGlobalShortcutsDataRelay } from './useGlobalShortcutsDataRelay';

/**
 * @param input.shortcutsConfig - Configuration for global shortcuts. Accepts either a `ShortcutsConfig` record or a `GlobalShortcutsItem[]` array; the return type matches the input shape.
 * @returns Shortcuts config with URLs hydrated from Relay (`appSwitcher_availableProductsBySite`).
 */
export const useGlobalShortcutsDataQuery = <
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	shortcutsConfig,
	cloudId,
	product,
	onUpdateGetFilteredProductLinks,
	isShortcutsStatic,
	onError,
}: UseGlobalShortcutDataInputBase & { shortcutsConfig: T }): InferShortcutsOutput<T> => {
	const variables = useMemo(
		() => ({
			cloudId: cloudId ?? '',
			fetchGlobalShortcutsAvailableProducts:
				Boolean(cloudId) && !isShortcutsStatic && !isEmpty(shortcutsConfig),
		}),
		[cloudId, shortcutsConfig, isShortcutsStatic],
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const queryRef = useLazyLoadQuery<useGlobalShortcutsDataQueryType>(
		graphql`
			query useGlobalShortcutsDataQuery(
				$cloudId: ID!
				$fetchGlobalShortcutsAvailableProducts: Boolean!
			) {
				...useGlobalShortcutsDataRelay @include(if: $fetchGlobalShortcutsAvailableProducts)
			}
		`,
		variables,
	);

	const shortcutItems = useGlobalShortcutsDataRelay({
		queryRef,
		cloudId,
		onError,
		product,
		onUpdateGetFilteredProductLinks,
		shortcutsConfig,
	});

	return shortcutItems as InferShortcutsOutput<T>;
};
